/**
 * Kanban — Página principal (0258 Fase D)
 * Usa useKanbanData, useKanbanSocket, KanbanBanner, KanbanIndicator.
 */
import React, { useContext, useState } from "react";
import ConfirmationModal from "../../components/ConfirmationModal";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { deleteTicketTags, assignTicketTag } from "../../services/ticketTags.api";
import { updateAppointmentStatus } from "../../services/appointments.api";
import { reorderKanbanColumns } from "../../services/tagGroups.api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import KanbanBoard from "../../components/KanbanBoard";
import { KanbanBanner, KanbanIndicator } from "../../components/Kanban";
import useKanbanData from "./hooks/useKanbanData";
import useKanbanSocket from "./hooks/useKanbanSocket";
import logger from "../../utils/logger";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    height: "100%",
  },
  boardWrapper: {
    flex: 1,
    minHeight: 0,
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const {
    tags,
    setTags,
    tickets,
    setTickets,
    agendaActive,
    activeGroupName,
    isInitialized,
    refetch,
  } = useKanbanData();

  // Movimiento a la espera de confirmación: sólo tras aceptarlo se escribe al cliente.
  const [pendingMove, setPendingMove] = useState(null);

  useKanbanSocket({ isInitialized, user, setTags, setTickets, refetch });

  /**
   * 0391 F3: la columna destino se resuelve por CLAVE, no por su nombre.
   *
   * WHY: antes se adivinaba buscando subcadenas ("completada", "abandon", "paus"…).
   * Eso ata una acción que envía WhatsApps al texto de una etiqueta: en cuanto el
   * cliente pueda renombrar sus columnas (F4), el arrastre dejaría de funcionar, y un
   * nombre desafortunado podría disparar una notificación no pretendida.
   *
   * `interested` y `unassigned` devuelven null a propósito: no son estados de cita.
   */
  const STAGE_TO_STATUS = {
    pending_confirm: "pending",
    confirmed: "confirmed",
    in_progress: "in_progress",
    completed: "completed",
    cancelled: "cancelled",
  };

  /** Transiciones que envían un mensaje al cliente en el momento del arrastre. */
  const NOTIFYING_STATUS = {
    confirmed: "kanban.confirmMoveConfirmed",
    completed: "kanban.confirmMoveCompleted",
    cancelled: "kanban.confirmMoveCancelled",
  };

  /** Nombre del contacto de una tarjeta, para que el aviso diga a quién se va a escribir. */
  const ticketName = (cardId) => {
    const card = (tickets || []).find((t) => String(t.cardId || t.id) === String(cardId));
    return card?.contact?.name || card?.contactName || i18n.t("kanban.theContact");
  };

  const handleCardMove = async (cardId, sourceLaneId, targetLaneId) => {
    try {
      let targetTag = null;
      if (targetLaneId.startsWith("tag-")) {
        const targetTagId = parseInt(targetLaneId.replace("tag-", ""), 10);
        targetTag = tags.find((t) => t.id === targetTagId);
      }

      if (!targetTag) {
        toast.error(i18n.t("kanban.tagNotFound"));
        return;
      }

      // 0266: Si es card de cita (appointment-X), actualizar status de la cita
      if (typeof cardId === "string" && cardId.startsWith("appointment-")) {
        const appointmentId = parseInt(cardId.replace("appointment-", ""), 10);
        const newStatus = STAGE_TO_STATUS[targetTag.stageKey];
        if (!newStatus) {
          toast.error(i18n.t("kanban.cannotMapStatus"));
          return;
        }

        /**
         * Salvaguarda antes de hablar con el cliente (doc 0391 F3).
         *
         * El mensaje sale en el instante del arrastre y es irreversible: devolver la
         * tarjeta a su sitio no lo desdice. Y como el envío es idempotente, un segundo
         * arrastre a la misma columna NO reenviaría, lo que el operador leería como un
         * comportamiento errático. Mejor preguntar una vez.
         *
         * Se pregunta con el modal del producto, no con `window.confirm`: aquel puede
         * silenciarse en el navegador, y entonces la acción quedaría bloqueada sin que
         * nadie entienda por qué.
         */
        const confirmKey = NOTIFYING_STATUS[newStatus];
        if (confirmKey) {
          setPendingMove({
            appointmentId,
            newStatus,
            message: i18n.t(confirmKey, { name: ticketName(cardId) }),
          });
          return;
        }

        await updateAppointmentStatus(appointmentId, { status: newStatus });
        toast.success(i18n.t("kanban.appointmentUpdated"));
        refetch();
        return;
      }

      // Card de ticket (sin cita): actualizar tags del ticket
      const ticketId = typeof cardId === "string" && cardId.startsWith("ticket-")
        ? parseInt(cardId.replace("ticket-", ""), 10)
        : parseInt(cardId, 10);

      if (sourceLaneId && sourceLaneId !== targetLaneId) {
        try {
          await deleteTicketTags(ticketId);
        } catch (_) {}
      }

      await assignTicketTag(ticketId, targetTag.id);
      toast.success(i18n.t("kanban.ticketMoved"));
    } catch (err) {
      logger.dashboard.error("Error moviendo card:", err);
      // 409 = el servidor rechazó la transición (doc 0391 F3). Su mensaje explica por qué
      // ese salto no describe ningún hecho real; mostrarlo es más útil que un error genérico.
      if (err?.response?.status === 409) {
        toast.error(err.response.data?.error || i18n.t("kanban.moveNotAllowed"));
      } else {
        toast.error(i18n.t("kanban.moveError"));
      }
      refetch();
    }
  };

  const handleCardClick = (ticket) => {
    history.push(`/tickets/${ticket.uuid}`);
  };

  /**
   * Reordenar carriles desde el propio tablero (0391 · corrección B).
   *
   * Usa el mismo endpoint que la pantalla de Etiquetas, así que ambos sitios quedan
   * sincronizados sin lógica duplicada: los dos escriben `displayOrder`.
   */
  const handleColumnsReorder = async (tagIds) => {
    try {
      await reorderKanbanColumns(tagIds);
      toast.success(i18n.t("kanban.columnsReordered"));
      refetch();
    } catch (err) {
      toastError(err);
      refetch(); // deshace el movimiento visual si el servidor lo rechazó
    }
  };

  /** Confirma el movimiento pendiente y sólo entonces escribe al cliente. */
  const handleConfirmPendingMove = async () => {
    if (!pendingMove) return;
    const { appointmentId, newStatus } = pendingMove;
    setPendingMove(null);
    try {
      await updateAppointmentStatus(appointmentId, { status: newStatus });
      toast.success(i18n.t("kanban.appointmentUpdated"));
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error(err.response.data?.error || i18n.t("kanban.moveNotAllowed"));
      } else {
        toast.error(i18n.t("kanban.moveError"));
      }
    }
    refetch();
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("kanban.confirmMoveTitle")}
        open={!!pendingMove}
        onClose={() => { setPendingMove(null); refetch(); }}
        onConfirm={handleConfirmPendingMove}
      >
        {pendingMove?.message}
        {"\n\n"}
        {i18n.t("kanban.confirmMoveIrreversible")}
      </ConfirmationModal>
      {isInitialized && tags.length === 0 && <KanbanBanner />}
      {isInitialized && tags.length > 0 && <KanbanIndicator groupName={activeGroupName} agendaActive={agendaActive} />}
      <div className={classes.boardWrapper}>
        {tags.length > 0 ? (
          <KanbanBoard
            tickets={tickets}
            tags={tags}
            onCardMove={handleCardMove}
            onCardClick={handleCardClick}
            onColumnsReorder={handleColumnsReorder}
          />
        ) : (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Typography color="textSecondary">
              {i18n.t("kanban.configureAgendaHint")}
            </Typography>
          </div>
        )}

      </div>
    </div>
  );
};

export default Kanban;
